/**
 * Step 7: Review & Submit
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { 
  Loader2, 
  ChevronLeft, 
  CheckCircle, 
  XCircle, 
  Building2, 
  Clock, 
  CreditCard,
  FileText,
  Image,
  Truck,
  Mail,
  Phone,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ReviewStep({ allData, status, onComplete, onBack }) {
  const [submitting, setSubmitting] = useState(false);
  const [sendingPhoneCode, setSendingPhoneCode] = useState(false);
  const [sendingEmailCode, setSendingEmailCode] = useState(false);
  const [phoneCode, setPhoneCode] = useState('');
  const [verifyingPhone, setVerifyingPhone] = useState(false);

  const sendPhoneVerification = async () => {
    if (!allData?.business_info?.store_phone) {
      toast.error('Please complete business information first');
      return;
    }

    setSendingPhoneCode(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/retailer/onboarding/phone/send-code`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ phone_number: allData.business_info.store_phone })
      });

      if (response.ok) {
        toast.success('Verification code sent!');
      } else {
        const result = await response.json();
        toast.error(result.detail || 'Failed to send code');
      }
    } catch (error) {
      toast.error('Failed to send verification code');
    } finally {
      setSendingPhoneCode(false);
    }
  };

  const verifyPhone = async () => {
    if (!phoneCode || phoneCode.length < 4) {
      toast.error('Please enter the verification code');
      return;
    }

    setVerifyingPhone(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/retailer/onboarding/phone/verify`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          phone_number: allData.business_info.store_phone,
          code: phoneCode
        })
      });

      if (response.ok) {
        toast.success('Phone verified!');
        // Refresh status
        window.location.reload();
      } else {
        const result = await response.json();
        toast.error(result.detail || 'Invalid code');
      }
    } catch (error) {
      toast.error('Verification failed');
    } finally {
      setVerifyingPhone(false);
    }
  };

  const sendEmailVerification = async () => {
    setSendingEmailCode(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/retailer/onboarding/email/send-verification`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Verification email sent! Check your inbox.');
      } else {
        const result = await response.json();
        toast.error(result.detail || 'Failed to send email');
      }
    } catch (error) {
      toast.error('Failed to send verification email');
    } finally {
      setSendingEmailCode(false);
    }
  };

  const handleSubmit = async () => {
    // Check verifications
    if (!status?.phone_verified) {
      toast.error('Please verify your phone number');
      return;
    }
    if (!status?.email_verified) {
      toast.error('Please verify your email address');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/retailer/onboarding/submit`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const result = await response.json();

      if (response.ok) {
        toast.success('Application submitted for review!');
        onComplete(result);
      } else {
        toast.error(result.detail?.message || result.detail || 'Submission failed');
      }
    } catch (error) {
      toast.error('Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  const SectionCard = ({ icon: Icon, title, complete, children }) => (
    <div className="border rounded-lg p-4">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-5 h-5 text-gray-600" />
        <h3 className="font-medium">{title}</h3>
        {complete ? (
          <CheckCircle className="w-4 h-4 text-emerald-500 ml-auto" />
        ) : (
          <XCircle className="w-4 h-4 text-red-400 ml-auto" />
        )}
      </div>
      {children}
    </div>
  );

  const canSubmit = status?.phone_verified && status?.email_verified;
  const missingRequired = [];
  if (!status?.business_info_complete) missingRequired.push('Business Info');
  if (!status?.store_hours_complete) missingRequired.push('Store Hours');
  if (!status?.bank_details_complete) missingRequired.push('Bank Details');
  if (!status?.delivery_config_complete) missingRequired.push('Delivery Config');

  return (
    <div className="space-y-6">
      <div className="text-center pb-4 border-b">
        <h2 className="text-xl font-bold text-gray-900">Review Your Application</h2>
        <p className="text-gray-600 mt-1">
          Please review your information and complete verifications before submitting.
        </p>
      </div>

      {/* Missing steps warning */}
      {missingRequired.length > 0 && (
        <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
          <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
          <div>
            <p className="font-medium text-amber-800">Missing Required Steps</p>
            <p className="text-sm text-amber-700">
              Please complete: {missingRequired.join(', ')}
            </p>
          </div>
        </div>
      )}

      {/* Business Info Summary */}
      <SectionCard 
        icon={Building2} 
        title="Business Information" 
        complete={status?.business_info_complete}
      >
        {allData?.business_info ? (
          <div className="text-sm text-gray-600 space-y-1">
            <p><strong>Store:</strong> {allData.business_info.store_name}</p>
            <p><strong>Address:</strong> {allData.business_info.formatted_address || 
              `${allData.business_info.store_address}, ${allData.business_info.store_city}, ${allData.business_info.store_state}`}
            </p>
            <p><strong>Phone:</strong> {allData.business_info.store_phone}</p>
          </div>
        ) : (
          <p className="text-sm text-gray-400">Not completed</p>
        )}
      </SectionCard>

      {/* Store Hours Summary */}
      <SectionCard 
        icon={Clock} 
        title="Store Hours" 
        complete={status?.store_hours_complete}
      >
        {allData?.store_hours ? (
          <div className="text-sm text-gray-600">
            <p>Timezone: {allData.store_hours.timezone}</p>
            <p className="text-xs text-gray-400 mt-1">Hours configured for all days</p>
          </div>
        ) : (
          <p className="text-sm text-gray-400">Not completed</p>
        )}
      </SectionCard>

      {/* Bank Details Summary */}
      <SectionCard 
        icon={CreditCard} 
        title="Bank/Payout Details" 
        complete={status?.bank_details_complete}
      >
        {allData?.bank_details ? (
          <div className="text-sm text-gray-600">
            {allData.bank_details.stripe_account_id ? (
              <Badge className="bg-purple-100 text-purple-700">Stripe Connected</Badge>
            ) : allData.bank_details.use_manual_bank ? (
              <p>Bank: {allData.bank_details.bank_name} (****{allData.bank_details.bank_account_last4})</p>
            ) : (
              <p className="text-amber-600">Setup incomplete</p>
            )}
          </div>
        ) : (
          <p className="text-sm text-gray-400">Not completed</p>
        )}
      </SectionCard>

      {/* Documents Summary */}
      <SectionCard 
        icon={FileText} 
        title="Documents" 
        complete={status?.documents_complete}
      >
        {allData?.documents?.documents?.length > 0 ? (
          <div className="text-sm text-gray-600">
            {allData.documents.documents.length} document(s) uploaded
          </div>
        ) : (
          <p className="text-sm text-gray-400">Skipped (optional)</p>
        )}
      </SectionCard>

      {/* Logo Summary */}
      <SectionCard 
        icon={Image} 
        title="Store Logo" 
        complete={status?.store_logo_complete}
      >
        {allData?.store_logo?.logo_storage_path ? (
          <Badge className="bg-emerald-100 text-emerald-700">Uploaded</Badge>
        ) : (
          <p className="text-sm text-gray-400">Skipped (optional)</p>
        )}
      </SectionCard>

      {/* Delivery Config Summary */}
      <SectionCard 
        icon={Truck} 
        title="Delivery Configuration" 
        complete={status?.delivery_config_complete}
      >
        {allData?.delivery_config ? (
          <div className="text-sm text-gray-600 space-y-1">
            <p>Prep time: {allData.delivery_config.prep_time_minutes} minutes</p>
            <p>
              {allData.delivery_config.accepts_delivery && 'Delivery '}
              {allData.delivery_config.accepts_delivery && allData.delivery_config.accepts_pickup && '& '}
              {allData.delivery_config.accepts_pickup && 'Pickup'}
            </p>
          </div>
        ) : (
          <p className="text-sm text-gray-400">Not completed</p>
        )}
      </SectionCard>

      {/* Verifications */}
      <div className="border-t pt-6">
        <h3 className="font-medium mb-4">Required Verifications</h3>
        
        {/* Phone Verification */}
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg mb-3">
          <div className="flex items-center gap-3">
            <Phone className="w-5 h-5 text-gray-600" />
            <div>
              <p className="font-medium">Phone Verification</p>
              <p className="text-sm text-gray-500">
                {allData?.business_info?.store_phone || 'No phone provided'}
              </p>
            </div>
          </div>
          {status?.phone_verified ? (
            <Badge className="bg-emerald-100 text-emerald-700">
              <CheckCircle className="w-3 h-3 mr-1" />
              Verified
            </Badge>
          ) : (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={phoneCode}
                onChange={(e) => setPhoneCode(e.target.value)}
                placeholder="Code"
                className="w-20 px-2 py-1 border rounded text-sm"
                maxLength={6}
              />
              <Button
                size="sm"
                variant="outline"
                onClick={sendPhoneVerification}
                disabled={sendingPhoneCode}
              >
                {sendingPhoneCode ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Send'}
              </Button>
              <Button
                size="sm"
                onClick={verifyPhone}
                disabled={verifyingPhone || !phoneCode}
              >
                {verifyingPhone ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Verify'}
              </Button>
            </div>
          )}
        </div>

        {/* Email Verification */}
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-3">
            <Mail className="w-5 h-5 text-gray-600" />
            <div>
              <p className="font-medium">Email Verification</p>
              <p className="text-sm text-gray-500">Check your email inbox</p>
            </div>
          </div>
          {status?.email_verified ? (
            <Badge className="bg-emerald-100 text-emerald-700">
              <CheckCircle className="w-3 h-3 mr-1" />
              Verified
            </Badge>
          ) : (
            <Button
              size="sm"
              variant="outline"
              onClick={sendEmailVerification}
              disabled={sendingEmailCode}
            >
              {sendingEmailCode ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
              Send Email
            </Button>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-between gap-3 pt-4 border-t">
        {onBack && (
          <Button type="button" variant="outline" onClick={onBack}>
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
        )}
        <Button
          onClick={handleSubmit}
          disabled={submitting || !canSubmit || missingRequired.length > 0}
          className="bg-emerald-600 hover:bg-emerald-700 ml-auto"
          data-testid="submit-onboarding-btn"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Submitting...
            </>
          ) : (
            'Submit for Approval'
          )}
        </Button>
      </div>
    </div>
  );
}
